// The signed-out pages' frame and parts (04-sign-in.md, "The frame of every signed-out page",
// "Parts of the card", "The wait"): desyr.chat's look, shared with set-up and recovery (27).
import { useEffect, useId, useRef, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import logo from '../assets/logo.svg';
import { Icon, icons } from '../blocks/icons';
import type { Config } from '../core/config';
import { PRODUCT } from '../core/product';
import { Link } from '../core/router';
import { OpenSource } from '../legal/OpenSource';
import { InstallOffer } from '../install/install';
import { chooseLanguage, currentLanguage, LANGUAGES, t, type Language } from '../i18n';

/** The wait shows only after 250 ms, and then for at least 400 ms, so it never flickers. */
export function useWait(active: boolean): boolean {
  const [shown, setShown] = useState(false);
  const since = useRef(0);
  useEffect(() => {
    if (active) {
      const t = setTimeout(() => {
        since.current = Date.now();
        setShown(true);
      }, 250);
      return () => clearTimeout(t);
    }
    const left = 400 - (Date.now() - since.current);
    const t = setTimeout(() => setShown(false), Math.max(0, left));
    return () => clearTimeout(t);
  }, [active]);
  return shown;
}

/** A legal page's link: the operator's own https address (new tab) or desyr's page (31-legal.md). */
function LegalLink({ config, doc, children }: { config: Config; doc: 'privacy' | 'terms' | 'imprint'; children: string }) {
  const own = config.desyrLegal?.[doc];
  if (own && /^(https:\/\/|\/)/.test(own))
    return (
      <a href={own} target="_blank" rel="noreferrer">
        {children}
      </a>
    );
  return <Link to={`/legal/${doc}/`}>{children}</Link>;
}

/** The switch at the top right (04): "DE" named "Deutsch" while English shows, and back. */
function LanguageSwitch() {
  const other: Language = currentLanguage() === 'de' ? 'en' : 'de';
  return (
    <button type="button" className="signin-language" lang={other} aria-label={LANGUAGES[other].name} onClick={() => void chooseLanguage(other)}>
      {other.toUpperCase()}
    </button>
  );
}

export function Frame({ config, waiting, step, children }: { config: Config; waiting?: boolean; step: string; children: ReactNode }) {
  const [openSource, setOpenSource] = useState(false);
  return (
    <div className="signin si">
      <LanguageSwitch />
      <div className={waiting ? 'signin-logo waiting' : 'signin-logo'}>
        <img src={logo} alt={t('signin.logo')} />
        <span className="wordmark">{PRODUCT}</span>
      </div>
      <main className="signin-card" aria-live="polite">
        <div className="signin-step" data-desyr-sign-in-step={step}>
          {children}
        </div>
      </main>
      <InstallOffer button />
      <footer className="signin-footer">
        <span>
          <Icon svg={icons.lock} size={16} className="lock" />
          {t('signin.private')}
        </span>
        <nav>
          <LegalLink config={config} doc="privacy">{t('signin.privacy')}</LegalLink>
          <LegalLink config={config} doc="terms">{t('signin.terms')}</LegalLink>
          <LegalLink config={config} doc="imprint">{t('signin.imprint')}</LegalLink>
          <a href="/licenses.txt" onClick={(e) => (e.preventDefault(), setOpenSource(true))}>
            {t('legal.openSource')}
          </a>
        </nav>
      </footer>
      {openSource && <OpenSource onClose={() => setOpenSource(false)} />}
    </div>
  );
}

/** A step's heading, level 1; it takes the focus when the step has no field. */
export const Heading = ({ children, sub }: { children: ReactNode; sub?: ReactNode }) => (
  <header className="signin-heading">
    <h1 tabIndex={-1}>{children}</h1>
    {sub && <p>{sub}</p>}
  </header>
);

export interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  /** Beside the label, at the right (e.g. "Forgot password?"). */
  aside?: ReactNode;
  error?: ReactNode;
  invalid?: boolean;
  /** An eye button that shows the password. */
  reveal?: boolean;
}

export function Field({ label, aside, error, invalid, reveal, className, type, ...input }: FieldProps) {
  const id = useId();
  const [shown, setShown] = useState(false);
  return (
    <div className="si-field">
      <div className="si-label-row">
        <label htmlFor={id}>{label}</label>
        {aside}
      </div>
      <div className={`si-input${invalid ? ' invalid' : ''}${className ? ` ${className}` : ''}`}>
        <input id={id} type={reveal && shown ? 'text' : type} aria-invalid={invalid || undefined} {...input} />
        {reveal && (
          <button type="button" className="si-eye" tabIndex={-1} aria-label={t(shown ? 'signin.hide' : 'signin.show')} onClick={() => setShown(!shown)}>
            <Icon svg={shown ? icons.visibilityOff : icons.visibility} size={20} />
          </button>
        )}
      </div>
      <ErrorLine>{error}</ErrorLine>
    </div>
  );
}

export const ErrorLine = ({ children }: { children?: ReactNode }) =>
  children ? (
    <p className="si-error" role="alert" data-desyr-sign-in-error="">
      {children}
    </p>
  ) : null;

/** The main button: coral with ink; while waiting the label fades and three dots blink. */
export function MainButton({ waiting, children, ...rest }: { waiting?: boolean; children: string } & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button type="submit" className={waiting ? 'si-main waiting' : 'si-main'} aria-busy={waiting || undefined} {...rest}>
      <span className="si-main-label">{children}</span>
      {waiting && (
        <span className="si-dots" aria-hidden="true">
          <i />
          <i />
          <i />
        </span>
      )}
    </button>
  );
}

/** The error and "sent" marks above a heading. */
export const Mark = ({ kind }: { kind: 'error' | 'sent' }) => (
  <span className={`si-mark ${kind}`} aria-hidden="true">
    <Icon svg={kind === 'error' ? icons.warning : icons.check} size={24} />
  </span>
);
